import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './Button';
import { useHistoryLayer } from './history';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Bottom sheet on phones, centred dialog on laptops. Traps focus, closes on Escape
 * or a tap on the scrim, restores focus to whatever opened it, and locks page scroll.
 */
export function Sheet({
  title,
  subtitle,
  media,
  onClose,
  children,
  wide,
  side,
  history = true,
  closeLabel = 'Close',
  initialFocus,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  media?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  /** On laptops, slide in from the right as a drawer instead of a centred dialog. */
  side?: boolean;
  /** Back button closes the sheet (default). Off when the caller drives history itself. */
  history?: boolean;
  closeLabel?: string;
  /** CSS selector inside the sheet to focus first. */
  initialFocus?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useHistoryLayer(history, () => closeRef.current());

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const node = ref.current!;
    const first =
      (initialFocus && node.querySelector<HTMLElement>(initialFocus)) ||
      node.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
      } else if (e.key === 'Tab') {
        const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)];
        if (!items.length) return;
        const [a, z] = [items[0]!, items[items.length - 1]!];
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          z.focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          a.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <div
      className={`scrim${side ? ' scrim--side' : ''}`}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className={`sheet${wide ? ' sheet--wide' : ''}${side ? ' sheet--side' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="sheet__head">
          {media ?? <span />}
          <div>
            <h2 className="sheet__title" id={titleId}>
              {title}
            </h2>
            {subtitle && <div className="sheet__sub">{subtitle}</div>}
          </div>
          <IconButton icon="x" label={closeLabel} onClick={onClose} />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
